import {identity} from 'lodash';
import {applyMiddleware, createStore, DeepPartial, Store, StoreEnhancer} from 'redux';
import freeze from 'redux-freeze';

import {isDevelopment} from '../../libs/shared/helpers/src/environment/envHelpers';
import {composeEnhancers} from '../helpers/reduxHelpers';
import {AppAction} from './appActions';
import {buildBatchedStore} from './appBatching';
import {appReducer} from './appReducer';
import {AppState} from './appState';
import {AppThunkDispatch, AppThunkExtension, appThunkMiddleware} from './appThunk';
import {devToolsEnhancer} from './devtoolsHelpers';

/*
 * Types.
 */

export type StateGetter = () => AppState;
export type Dispatch = AppThunkDispatch;
export type AppStore = Store<AppState, AppAction> & AppThunkExtension;

export type InitialAppState = DeepPartial<AppState> & Pick<AppState, 'data'>;

export interface CreateAppStoreOptions<Ext = {}> {
  initialState: InitialAppState;
  enhancer?: StoreEnhancer<Ext, {}>;
}

/*
 * Store.
 */

export function createAppStore<Ext = {}>(options: CreateAppStoreOptions<Ext>): AppStore {
  const enhancer = composeEnhancers(
    applyMiddleware(appThunkMiddleware, ...makeDevMiddleware()),
    options.enhancer || identity,
    devToolsEnhancer as StoreEnhancer<{}>,
  );

  const unbatchedStore = createStore(appReducer, options.initialState, enhancer);
  return buildBatchedStore(unbatchedStore);
}

function makeDevMiddleware() {
  if (!isDevelopment()) {
    return [];
  }

  return [freeze];
}
